import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { EmailService } from '../../../services/EmailService';
import { Event } from '../../../services/EventService';
import { supabase } from '../../../config/supabase';
import { AuthModal } from '../../auth/components/AuthModal';
import { slugify } from '../../../utils/slugify';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CheckCircle, Info, AlertCircle, Calendar, Clock, MapPin, Mail, Users, Check } from 'lucide-react';

interface TokenValidation {
  isValid: boolean;
  email?: string;
  name?: string;
  error?: any;
}

const AcceptEventUserInvitationPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null);
  const [eventData, setEventData] = useState<any>(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: any; text: string } | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const token = searchParams.get('token');

  const initInvitationFlow = async () => {
    if (!token || !eventId) {
      setMessage({ type: 'error', text: 'Invalid invitation link. Missing parameters.' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Validating event user token...', { token, eventId });
      
      // Verificar token para event_users
      const validation = await EmailService.verifyEventUserToken(token, eventId);
      console.log('Token validation result:', validation);
      setTokenValidation(validation);

      if (!validation.isValid) {
        setMessage({ type: 'error', text: 'This invitation link is invalid or has expired.' });
        setLoading(false);
        return;
      }

      // Cargar datos del evento
      const { data: event, error: eventError } = await Event.getById(eventId);
      console.log('Event data result:', { event, eventError });
      if (eventError || !event) {
        setMessage({ type: 'error', text: 'The event associated with this invitation could not be found.' });
        setLoading(false);
        return;
      }
      setEventData(event);

      // Verificar si el usuario ya está registrado
      const { data: existingUser } = await supabase
        .from('event_users')
        .select('*')
        .eq('event_id', eventId)
        .eq('email', validation.email)
        .single();

      if (existingUser && existingUser.invitation_status === 'accepted') {
        setMessage({ 
          type: 'info', 
          text: `You're already registered for "${event.title}"! Redirecting to event page...` 
        });
        setTimeout(() => navigate(`/events/${slugify(event.title)}/${eventId}`), 3000);
        return;
      }

      if (user) {
        if (user.email !== validation.email) {
          setMessage({ 
            type: 'info', 
            text: `This invitation is for ${validation.email}. Please log in with the correct account.` 
          });
          setShowAuthModal(true);
          return;
        }
        
        await processInvitationAcceptance();
      } else {
        setShowAuthModal(true);
      }
    } catch (err) {
      console.error('Error in invitation flow:', err);
      setMessage({ type: 'error', text: 'An unexpected error occurred while processing your invitation.' });
    } finally {
      setLoading(false);
    }
  };

  const processInvitationAcceptance = async () => {
    if (!tokenValidation || !eventData) return;

    setProcessing(true);
    try {
      console.log('Processing invitation acceptance...');
      
      const { data, error } = await Event.acceptEventUserInvitation(
        eventId!,
        token!,
        user ? { 
          id: user.id, 
          name: user.user_metadata?.full_name || user.user_metadata?.name || tokenValidation.name,
          avatar_url: user.user_metadata?.avatar_url 
        } : undefined
      );

      if (error) {
        if (error.message?.includes('already accepted') || error.message?.includes('already registered')) {
          setMessage({
            type: 'info',
            text: `You're already registered for "${eventData.title}"! Redirecting...`
          });
        } else {
          setMessage({ type: 'error', text: 'Failed to accept invitation: ' + error.message });
          return;
        }
      } else {
        setMessage({
          type: 'success',
          text: `Welcome to "${eventData.title}"! You're now registered for this event.`
        });
      }

      // Marcar token como usado
      await supabase
        .from('event_users')
        .update({ invitation_token: null })
        .eq('invitation_token', token);

      // Redirigir al evento
      setTimeout(() => navigate(`/events/${slugify(eventData.title)}/${eventId}`), 3000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setMessage({ type: 'error', text: 'An unexpected error occurred while accepting your invitation.' });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    initInvitationFlow();
  }, [eventId, token, user]);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // El useEffect se re-ejecutará cuando cambie el user
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-medium">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-medium">Confirming your attendance...</p>
        </div>
      </div>
    );
  }

  if (message?.type === 'success' || message?.type === 'info') {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className={`w-16 h-16 ${message.type === 'success' ? 'bg-green-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <Info className="w-8 h-8 text-blue-600" />
              )}
            </div>
            <h2 className={`text-2xl font-bold mb-3 ${message.type === 'success' ? 'text-green-800' : 'text-blue-800'}`}>
              {message.type === 'success' ? 'Welcome!' : 'Info'}
            </h2>
            <p className={`mb-6 text-base ${message.type === 'success' ? 'text-green-700' : 'text-blue-700'}`}>
              {message.text}
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Redirecting to event page...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValidation?.isValid || !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-3">Invitation Error</h2>
            <p className="text-red-700 mb-6">
              {message?.text || 'This invitation link is invalid or has expired.'}
            </p>
            <div className="space-y-2">
              {eventData && (
                <Button
                  onClick={() => navigate(`/events/${slugify(eventData.title)}/${eventId}`)}
                  className="w-full"
                >
                  View Event Details
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen brand-soft-bg">
      <div className="max-w-2xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">You're Invited!</h1>
          <p className="text-xl text-gray-600">
            Join this amazing event and connect with like-minded people
          </p>
        </div>

        {/* Event Card */}
        <Card className="mb-8 border-0 shadow-lg overflow-hidden">
          {eventData.image_url && (
            <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 relative">
              <img 
                src={eventData.image_url} 
                alt={eventData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20" />
            </div>
          )}
          
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">{eventData.title}</CardTitle>
            {eventData.short_description && (
              <CardDescription className="text-base text-gray-600 mt-2">
                {eventData.short_description}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 text-gray-600">
                <Calendar className="w-5 h-5 text-blue-500" />
                <span className="font-medium">
                  {new Date(eventData.start_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="font-medium">{eventData.start_time}</span>
              </div>
              
              {eventData.location && (
                <div className="flex items-center space-x-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{eventData.location}</span>
                </div>
              )}
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Mail className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Invited as: {tokenValidation.email}</span>
              </div>
            </div>
            
            {eventData.limited_places && (
              <div className="flex items-center space-x-2 pt-2">
                <Users className="w-5 h-5 text-amber-500" />
                <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
                  Limited to {eventData.limited_places} places
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-blue-900">What you'll get:</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-blue-800">
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Access to all event activities and sessions</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Networking opportunities with other attendees</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Event updates and reminders</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Easy calendar integration</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Accept Invitation Button - ADD THIS SECTION */}
        {user && user.email === tokenValidation.email && (
          <Card className="mb-8 border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="text-center">
                <Button
                  onClick={processInvitationAcceptance}
                  disabled={processing}
                  style={{color: 'white'}}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg text-lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Confirming your attendance...
                    </>
                  ) : (
                    'Accept Invitation & Join Event'
                  )}
                </Button>
                <p className="text-sm text-gray-600 mt-2">
                  Click to confirm your attendance for this event
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Messages */}
        {message && message.type === "info" && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-blue-500 mr-2" />
                <p className="text-blue-700">{message.text}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Messages */}
        {message && message.type === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p className="text-red-700">{message.text}</p>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        <AuthModal
          skipRedirect={true}
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultEmail={tokenValidation?.email}
          onSuccess={handleAuthSuccess}
          title="Join this event"
          subtitle="Sign in or create an account to accept this invitation"
          redirectGoogleUri={`${window.location.origin}/events/${eventId}/accept?token=${token}`}
        />
      </div>
    </div>
  );
};

export default AcceptEventUserInvitationPage;